import { useCallback, useEffect, useMemo, useRef, useState, Fragment } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, ChevronDown, FileDown, FileText, Landmark, Loader2, Lock, MessageCircle, RefreshCw, Save, Scissors, Search, Unlock, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { computeEntry, rp, summarize } from "@/lib/payroll";
import { bankColor, rekParts } from "@/lib/bank";
import { useT } from "@/i18n";
import { TID } from "@/constants/testIds";
import { Switch } from "@/components/ui/switch";
import NumField from "@/components/NumField";
import { JaritBadge } from "@/components/JaritBadge";
import { EmployeeName } from "@/components/EmployeeName";
import ItemsDialog from "@/components/ItemsDialog";
import FingerspotSyncPanel from "@/components/FingerspotSyncPanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const HEAD = [
  ["Employee", "text-left"],
  ["Rate / Hari", "text-right"],
  ["Hari", "text-center"],
  ["Upah", "text-right"],
  ["Bonus", "text-right"],
  ["Pendapatan Lain", "text-right"],
  ["BPJS", "text-center"],
  ["Kasbon", "text-right"],
  ["Potongan Lain", "text-right"],
  ["Total", "text-right"],
  ["", "text-center"],
];

const ItemsButton = ({ testid, value, onClick, disabled }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    disabled={disabled}
    className="num h-9 w-[112px] rounded-xl bg-[#F2F2F7] px-3 text-right text-[13.5px] font-medium transition-colors hover:bg-[#E7E7EC] disabled:cursor-not-allowed disabled:opacity-60"
  >
    {value ? rp(value) : "+"}
  </button>
);

const Row = ({ r, update, onPendapatan, onPotongan, onSlip, onWa, readOnly }) => (
  <tr className="border-b border-black/[0.04] transition-colors last:border-0 hover:bg-[#FAFAFA]">
    <td className="px-3 py-2.5">
      <p className="whitespace-nowrap text-[14px] font-medium"><EmployeeName id={r.employee_id} name={r.nama} /></p>
      {r.emp_code && <p className="num text-[11px] font-medium text-[#0071E3]">{r.emp_code}</p>}
      {r.metode === "transfer" ? (
        <p className="num text-[11.5px] text-[#8E8E93]">
          <span className="font-semibold" style={{ color: bankColor(r.bank) }}>
            {(r.bank || "").toUpperCase()}
          </span>{" "}
          {rekParts(r.no_rekening).head}
          <strong className="font-semibold text-[#1D1D1F]">
            {rekParts(r.no_rekening).tail}
          </strong>
        </p>
      ) : (
        <p className="num text-[11.5px] text-[#8E8E93]">Cash</p>
      )}
    </td>
    <td className="num px-3 py-2.5 text-right text-[13.5px] text-[#6E6E73]">
      {rp(r.rate_harian)}
    </td>
    <td className="px-3 py-2.5 text-center">
      <NumField
        testid={TID.payroll.hari(r.employee_id)}
        value={r.hari_kerja}
        onChange={(v) => update(r.employee_id, { hari_kerja: v })}
        align="text-center"
        decimal
        disabled={readOnly}
        className="h-9 w-16 text-[13.5px]"
      />
    </td>
    <td className="num px-3 py-2.5 text-right text-[14px] font-medium">{rp(r.upah)}</td>
    <td className="px-3 py-2.5 text-right">
      <NumField
        testid={TID.payroll.bonus(r.employee_id)}
        value={r.bonus}
        onChange={(v) => update(r.employee_id, { bonus_manual: v })}
        disabled={readOnly}
        className="h-9 w-[96px] text-[13.5px]"
      />
    </td>
    <td className="px-3 py-2.5 text-right">
      <ItemsButton
        testid={TID.payroll.pendapatanOpen(r.employee_id)}
        value={r.pendapatan_lain_total}
        onClick={() => onPendapatan(r.employee_id)}
        disabled={readOnly}
      />
    </td>
    <td className="px-3 py-2.5 text-center">
      <Switch
        data-testid={TID.payroll.bpjs(r.employee_id)}
        checked={!!r.bpjs}
        disabled={readOnly}
        onCheckedChange={(v) => update(r.employee_id, { bpjs: v })}
      />
    </td>
    <td className="px-3 py-2.5 text-right">
      <NumField
        testid={TID.payroll.kasbon(r.employee_id)}
        value={r.kasbon}
        onChange={(v) => update(r.employee_id, { kasbon: v })}
        disabled={readOnly}
        className="h-9 w-[96px] text-[13.5px]"
      />
    </td>
    <td className="px-3 py-2.5 text-right">
      <ItemsButton
        testid={TID.payroll.potonganOpen(r.employee_id)}
        value={r.potongan_lain_total}
        onClick={() => onPotongan(r.employee_id)}
        disabled={readOnly}
      />
    </td>
    <td
      data-testid={TID.payroll.total(r.employee_id)}
      className="num whitespace-nowrap px-3 py-2.5 text-right text-[14.5px] font-semibold"
    >
      {rp(r.total)}
    </td>
    <td className="px-3 py-2.5">
      <div className="flex items-center justify-center gap-1">
        <button
          data-testid={TID.payroll.slipOne(r.employee_id)}
          onClick={() => onSlip(r.employee_id)}
          className="grid h-8 w-8 place-items-center rounded-full text-[#8E8E93] transition-colors hover:bg-[#F2F2F7] hover:text-[#1D1D1F]"
          aria-label="Download slip"
        >
          <FileText className="h-4 w-4" />
        </button>
        <button
          data-testid={TID.payroll.waOne(r.employee_id)}
          onClick={() => onWa(r)}
          className="grid h-8 w-8 place-items-center rounded-full text-[#8E8E93] transition-colors hover:bg-[#E8F8EC] hover:text-[#25D366]"
          aria-label="Send WhatsApp"
        >
          <MessageCircle className="h-4 w-4" />
        </button>
      </div>
    </td>
  </tr>
);

const useIsMobile = () => {
  const [m, setM] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const on = () => setM(window.innerWidth < 768);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return m;
};

const FLBL = "text-[11px] font-medium uppercase tracking-wide text-[#8E8E93]";

const RowCard = ({ r, update, onPendapatan, onPotongan, onSlip, onWa, readOnly }) => (
  <div className="border-b border-black/[0.06] px-4 py-4 last:border-0">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate text-[15px] font-semibold"><EmployeeName id={r.employee_id} name={r.nama} /></p>
        {r.emp_code && <p className="num text-[12px] font-medium text-[#0071E3]">{r.emp_code}</p>}
        {r.metode === "transfer" ? (
          <p className="num text-[12px] text-[#8E8E93]">
            <span className="font-semibold" style={{ color: bankColor(r.bank) }}>
              {(r.bank || "").toUpperCase()}
            </span>{" "}
            {rekParts(r.no_rekening).head}
            <strong className="font-semibold text-[#1D1D1F]">
              {rekParts(r.no_rekening).tail}
            </strong>
          </p>
        ) : (
          <p className="text-[12px] text-[#8E8E93]">Cash</p>
        )}
      </div>
      <div className="shrink-0 text-right">
        <p className={FLBL}>Total</p>
        <p
          data-testid={TID.payroll.total(r.employee_id)}
          className="num text-[16px] font-semibold"
        >
          {rp(r.total)}
        </p>
      </div>
    </div>

    <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-3">
      <div className="flex flex-col gap-1">
        <span className={FLBL}>Hari kerja</span>
        <NumField
          testid={TID.payroll.hari(r.employee_id)}
          value={r.hari_kerja}
          onChange={(v) => update(r.employee_id, { hari_kerja: v })}
          align="text-left"
          decimal
          disabled={readOnly}
          className="h-10 w-full text-[14px]"
        />
      </div>
      <div className="flex flex-col gap-1">
        <span className={FLBL}>Bonus</span>
        <NumField
          testid={TID.payroll.bonus(r.employee_id)}
          value={r.bonus}
          onChange={(v) => update(r.employee_id, { bonus_manual: v })}
          align="text-left"
          disabled={readOnly}
          className="h-10 w-full text-[14px]"
        />
      </div>
      <div className="flex flex-col gap-1">
        <span className={FLBL}>Kasbon</span>
        <NumField
          testid={TID.payroll.kasbon(r.employee_id)}
          value={r.kasbon}
          onChange={(v) => update(r.employee_id, { kasbon: v })}
          align="text-left"
          disabled={readOnly}
          className="h-10 w-full text-[14px]"
        />
      </div>
      <div className="flex flex-col gap-1">
        <span className={FLBL}>Rate · Upah</span>
        <div className="num flex h-10 items-center text-[13px] text-[#6E6E73]">
          {rp(r.rate_harian)}
          <span className="mx-1">·</span>
          <span className="font-medium text-[#1D1D1F]">{rp(r.upah)}</span>
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <span className={FLBL}>Pendapatan lain</span>
        <button
          data-testid={TID.payroll.pendapatanOpen(r.employee_id)}
          onClick={() => onPendapatan(r.employee_id)}
          disabled={readOnly}
          className="num h-10 w-full rounded-xl bg-[#F2F2F7] px-3 text-left text-[13.5px] font-medium disabled:cursor-not-allowed disabled:opacity-60"
        >
          {r.pendapatan_lain_total ? rp(r.pendapatan_lain_total) : "+ Tambah"}
        </button>
      </div>
      <div className="flex flex-col gap-1">
        <span className={FLBL}>Potongan lain</span>
        <button
          data-testid={TID.payroll.potonganOpen(r.employee_id)}
          onClick={() => onPotongan(r.employee_id)}
          disabled={readOnly}
          className="num h-10 w-full rounded-xl bg-[#F2F2F7] px-3 text-left text-[13.5px] font-medium disabled:cursor-not-allowed disabled:opacity-60"
        >
          {r.potongan_lain_total ? rp(r.potongan_lain_total) : "+ Tambah"}
        </button>
      </div>
    </div>

    <div className="mt-3 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className={FLBL}>BPJS</span>
        <Switch
          data-testid={TID.payroll.bpjs(r.employee_id)}
          checked={!!r.bpjs}
          disabled={readOnly}
          onCheckedChange={(v) => update(r.employee_id, { bpjs: v })}
        />
      </div>
      <div className="flex items-center gap-2">
        <button
          data-testid={TID.payroll.slipOne(r.employee_id)}
          onClick={() => onSlip(r.employee_id)}
          className="flex h-9 items-center gap-1.5 rounded-full bg-[#F2F2F7] px-3.5 text-[13px] font-medium"
        >
          <FileText className="h-4 w-4" /> Slip
        </button>
        <button
          data-testid={TID.payroll.waOne(r.employee_id)}
          onClick={() => onWa(r)}
          className="flex h-9 items-center gap-1.5 rounded-full bg-[#E8F8EC] px-3.5 text-[13px] font-medium text-[#128C3E]"
        >
          <MessageCircle className="h-4 w-4" /> WA
        </button>
      </div>
    </div>
  </div>
);

const Section = ({ title, list, mobile, ...handlers }) =>
  list.length === 0 ? null : (
    <section className="card-soft mt-5 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5">
        <h3 className="text-[15px] font-semibold tracking-tight">{title}</h3>
        <span className="num text-[15px] font-semibold">
          Rp {rp(list.reduce((a, r) => a + r.total, 0))}
        </span>
      </div>
      {mobile ? (
        <div className="border-t border-black/[0.06]">
          {list.map((r) => (
            <RowCard key={r.employee_id} r={r} {...handlers} />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto border-t border-black/[0.06]">
          <table className="w-full min-w-[1180px] border-collapse">
            <thead>
              <tr className="border-b border-black/[0.06]">
                {HEAD.map(([h, align], i) => (
                  <th key={h + i} className={`label whitespace-nowrap px-3 py-2.5 ${align}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <Row key={r.employee_id} r={r} {...handlers} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );

const JARIT_HEAD = [
  ["Tukang Jarit", "text-left"],
  ["Ongkos Jarit", "text-right"],
  ["Bonus", "text-right"],
  ["BPJS", "text-center"],
  ["Kasbon", "text-right"],
  ["Diterima", "text-right"],
  ["", "text-center"],
];

const JaritRow = ({ r, onSlip, onWa }) => (
  <tr className="border-b border-black/[0.04] transition-colors last:border-0 hover:bg-[#FAFAFA]">
    <td className="px-3 py-2.5">
      <p className="whitespace-nowrap text-[14px] font-medium"><EmployeeName id={r.employee_id} name={r.nama} /></p>
      {r.emp_code && <p className="num text-[11px] font-medium text-[#0071E3]">{r.emp_code}</p>}
      <p className="text-[11.5px] text-[#8E8E93]">Ongkos Jarit · {(r.jarit_items || []).length} jenis barang</p>
    </td>
    <td className="num px-3 py-2.5 text-right text-[14px] font-medium">{rp(r.upah)}</td>
    <td className="num px-3 py-2.5 text-right text-[13.5px]">{rp(r.bonus)}</td>
    <td className="px-3 py-2.5 text-center text-[13px] text-[#6E6E73]">
      {r.bpjs_potongan ? `-${rp(r.bpjs_potongan)}` : "—"}
    </td>
    <td className="num px-3 py-2.5 text-right text-[13.5px] text-[#C8102E]">
      {r.kasbon ? `-${rp(r.kasbon)}` : "0"}
    </td>
    <td
      data-testid={TID.payroll.total(r.employee_id)}
      className="num whitespace-nowrap px-3 py-2.5 text-right text-[14.5px] font-semibold"
    >
      {rp(r.total)}
    </td>
    <td className="px-3 py-2.5">
      <div className="flex items-center justify-center gap-1">
        <button
          data-testid={TID.payroll.slipOne(r.employee_id)}
          onClick={() => onSlip(r.employee_id)}
          className="grid h-8 w-8 place-items-center rounded-full text-[#8E8E93] transition-colors hover:bg-[#F2F2F7] hover:text-[#1D1D1F]"
          aria-label="Download slip"
        >
          <FileText className="h-4 w-4" />
        </button>
        <button
          data-testid={TID.payroll.waOne(r.employee_id)}
          onClick={() => onWa(r)}
          className="grid h-8 w-8 place-items-center rounded-full text-[#8E8E93] transition-colors hover:bg-[#E8F8EC] hover:text-[#25D366]"
          aria-label="Send WhatsApp"
        >
          <MessageCircle className="h-4 w-4" />
        </button>
      </div>
    </td>
  </tr>
);

const JaritRowCard = ({ r, onSlip, onWa }) => (
  <div className="border-b border-black/[0.06] px-4 py-4 last:border-0">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate text-[15px] font-semibold"><EmployeeName id={r.employee_id} name={r.nama} /></p>
        {r.emp_code && <p className="num text-[12px] font-medium text-[#0071E3]">{r.emp_code}</p>}
        <p className="text-[12px] text-[#8E8E93]">Ongkos Jarit · {(r.jarit_items || []).length} jenis</p>
      </div>
      <div className="shrink-0 text-right">
        <p className={FLBL}>Diterima</p>
        <p data-testid={TID.payroll.total(r.employee_id)} className="num text-[16px] font-semibold">
          {rp(r.total)}
        </p>
      </div>
    </div>
    <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-[13.5px]">
      <div className="flex justify-between"><span className="text-[#8E8E93]">Ongkos Jarit</span><span className="num font-medium">{rp(r.upah)}</span></div>
      <div className="flex justify-between"><span className="text-[#8E8E93]">Bonus</span><span className="num">{rp(r.bonus)}</span></div>
      <div className="flex justify-between"><span className="text-[#8E8E93]">BPJS</span><span className="num text-[#C8102E]">{r.bpjs_potongan ? `-${rp(r.bpjs_potongan)}` : "—"}</span></div>
      <div className="flex justify-between"><span className="text-[#8E8E93]">Kasbon</span><span className="num text-[#C8102E]">{r.kasbon ? `-${rp(r.kasbon)}` : "0"}</span></div>
    </div>
    <div className="mt-3 flex items-center justify-end gap-2">
      <button
        data-testid={TID.payroll.slipOne(r.employee_id)}
        onClick={() => onSlip(r.employee_id)}
        className="flex h-9 items-center gap-1.5 rounded-full bg-[#F2F2F7] px-3.5 text-[13px] font-medium"
      >
        <FileText className="h-4 w-4" /> Slip
      </button>
      <button
        data-testid={TID.payroll.waOne(r.employee_id)}
        onClick={() => onWa(r)}
        className="flex h-9 items-center gap-1.5 rounded-full bg-[#E8F8EC] px-3.5 text-[13px] font-medium text-[#128C3E]"
      >
        <MessageCircle className="h-4 w-4" /> WA
      </button>
    </div>
  </div>
);

const JaritSection = ({ list, mobile, onSlip, onWa }) =>
  list.length === 0 ? null : (
    <section className="card-soft mt-5 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5">
        <div className="flex items-center gap-2.5">
          <h3 className="text-[15px] font-semibold tracking-tight">Tukang Jarit</h3>
          <Link
            to="/jarit"
            data-testid={TID.payroll.jaritManage}
            className="flex items-center gap-1 rounded-full bg-[#F2F2F7] px-2.5 py-1 text-[11.5px] font-medium text-[#6E6E73] transition-colors hover:bg-[#E7E7EC] hover:text-[#1D1D1F]"
          >
            <Scissors className="h-3 w-3" /> Kelola input di halaman Tukang Jarit
          </Link>
          <span className="flex items-center gap-1 text-[11px] font-medium text-[#128C3E]">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#25D366]" />
            diperbarui otomatis
          </span>
        </div>
        <span className="num text-[15px] font-semibold">
          Rp {rp(list.reduce((a, r) => a + r.total, 0))}
        </span>
      </div>
      {mobile ? (
        <div className="border-t border-black/[0.06]">
          {list.map((r) => (
            <JaritRowCard key={r.employee_id} r={r} onSlip={onSlip} onWa={onWa} />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto border-t border-black/[0.06]">
          <table className="w-full min-w-[720px] border-collapse">
            <thead>
              <tr className="border-b border-black/[0.06]">
                {JARIT_HEAD.map(([h, align], i) => (
                  <th key={h + i} className={`label whitespace-nowrap px-3 py-2.5 ${align}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <JaritRow key={r.employee_id} r={r} onSlip={onSlip} onWa={onWa} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );

const BcaExportDialog = ({ periodId, open, onOpenChange }) => {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});
  const isMobile = useIsMobile();

  const loadPreview = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await api.get(`/periods/${periodId}/bca/preview`, { params: { date: d } });
      setData(res.data);
    } catch (e) {
      toast.error(errMsg(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [periodId]);

  useEffect(() => {
    if (open) loadPreview(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onDate = (v) => {
    setDate(v);
    if (open) loadPreview(v);
  };

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/periods/${periodId}/bca/xlsx`, {
        params: { date }, responseType: "blob",
      });
      const disp = res.headers["content-disposition"] || "";
      const m = disp.match(/filename="?([^"]+)"?/);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data);
      link.download = m ? m[1] : "BCA-Payroll.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("BCA payroll file downloaded");
      loadPreview(date);
    } catch (e) {
      // blob error responses need to be read as text
      let msg = errMsg(e);
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          msg = JSON.parse(txt).detail || msg;
        }
      } catch { /* keep default */ }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const hasErrors = data?.errors?.length > 0;
  const canDownload = data && !hasErrors && data.transfer_count > 0 && !busy;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={TID.bca.dialog}
        className="max-h-[92vh] gap-0 overflow-y-auto rounded-[20px] border border-white/50 bg-white/70 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.18)] ring-1 ring-inset ring-white/50 backdrop-blur-2xl backdrop-saturate-150 sm:max-w-[720px]"
      >
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2.5 text-[19px] font-semibold tracking-tight">
            <img src="/bca-logo.webp" alt="BCA" className="h-8 w-auto" /> Multi Payroll Export
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-1.5">
            <label className="label block">Transfer date</label>
            <input
              data-testid={TID.bca.date}
              type="date"
              value={date}
              onChange={(e) => onDate(e.target.value)}
              className="focus-ring h-11 rounded-xl border border-[#E1E1E6] bg-[#FAFAFA] px-3.5 text-[15px]"
            />
          </div>
          {data && (
            <div data-testid={TID.bca.summary} className="grid grid-cols-2 gap-x-5 gap-y-3 sm:flex sm:gap-5 sm:text-right">
              <div>
                <p className="label">Transfers</p>
                <p className="num text-[20px] font-semibold">{data.transfer_count}</p>
              </div>
              <div>
                <p className="label">Cash (excluded)</p>
                <p className="num text-[20px] font-semibold text-[#8E8E93]">{data.cash_count}</p>
              </div>
              {data.non_bca_count > 0 && (
                <div>
                  <p className="label">Non-BCA (excluded)</p>
                  <p className="num text-[20px] font-semibold text-[#8E8E93]">{data.non_bca_count}</p>
                </div>
              )}
              <div>
                <p className="label">Total</p>
                <p className="num text-[20px] font-semibold text-[#0071E3]">Rp {rp(data.total_amount)}</p>
              </div>
            </div>
          )}
        </div>

        {data?.already_generated && (
          <p className="mt-3 rounded-xl bg-[#EEF3FA] px-3.5 py-2.5 text-[13px] text-[#0071E3]">
            Transaction IDs sudah pernah dibuat untuk periode ini — download ulang memakai ID yang sama.
          </p>
        )}

        {data?.non_bca_count > 0 && (
          <p className="mt-3 rounded-xl bg-[#FFF6E5] px-3.5 py-2.5 text-[13px] text-[#8A5A00]">
            {data.non_bca_count} karyawan transfer bank non-BCA dikecualikan — BCA multi payroll hanya mendukung transfer antar-rekening BCA.
          </p>
        )}

        {loading ? (
          <div className="flex items-center gap-2 py-10 text-[14px] text-[#8E8E93]">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading preview…
          </div>
        ) : hasErrors ? (
          <div data-testid={TID.bca.errors} className="mt-4 rounded-xl border border-[#F3C6CE] bg-[#FDF0F2] p-4">
            <p className="text-[14px] font-semibold text-[#C8102E]">
              Perbaiki {data.errors.length} masalah sebelum export:
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-[13px] text-[#C8102E]">
              {data.errors.map((er, i) => (
                <li key={i}>{er}</li>
              ))}
            </ul>
          </div>
        ) : data && data.transfer_count === 0 ? (
          <p className="mt-4 rounded-xl bg-[#F2F2F7] px-3.5 py-6 text-center text-[14px] text-[#8E8E93]">
            Tidak ada karyawan transfer bank pada periode ini.
          </p>
        ) : data ? (
          isMobile ? (
            <div data-testid={TID.bca.rows} className="mt-4 space-y-2">
              {data.rows.map((r, i) => (
                <div key={i} className="rounded-xl border border-[#EDEDF0] bg-white/60 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <p className="min-w-0 flex-1 break-words text-[14px] font-medium">{r.receiver_name}</p>
                    <p className="num shrink-0 text-[14px] font-semibold text-[#0071E3]">Rp {rp(r.amount)}</p>
                  </div>
                  <p className="num mt-1 text-[12px] text-[#8E8E93]">
                    {r.bank} · Type {r.transfer_type} · {r.credited_account}
                  </p>
                  {r.grouped && (
                    <>
                      <button
                        type="button"
                        data-testid={`bca-group-toggle-${i}`}
                        onClick={() => setExpanded((s) => ({ ...s, [i]: !s[i] }))}
                        className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#EEF3FA] px-2.5 py-1 text-[11px] font-medium text-[#0071E3]"
                      >
                        <Users className="h-3 w-3" /> Includes {r.member_count}
                        <ChevronDown className={`h-3 w-3 transition-transform ${expanded[i] ? "rotate-180" : ""}`} />
                      </button>
                      {expanded[i] &&
                        r.members.map((m, j) => (
                          <div key={`${i}-${j}`} className="mt-1.5 flex items-center justify-between gap-2 rounded-lg bg-[#FAFAFA] px-2.5 py-1.5 text-[12px] text-[#6E6E73]">
                            <span className="min-w-0 flex-1 break-words">↳ {m.nama}</span>
                            <span className="num shrink-0">Rp {rp(m.amount)}</span>
                          </div>
                        ))}
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
          <div data-testid={TID.bca.rows} className="mt-4 max-h-[46vh] overflow-y-auto rounded-xl border border-[#EDEDF0]">
            <table className="w-full text-[13px]">
              <thead className="sticky top-0 bg-[#F7F7FA] text-[#6E6E73]">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Name</th>
                  <th className="px-3 py-2 text-left font-medium">Bank</th>
                  <th className="px-3 py-2 text-left font-medium">Type</th>
                  <th className="px-3 py-2 text-left font-medium">Account</th>
                  <th className="px-3 py-2 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <Fragment key={i}>
                    <tr className="border-t border-[#F0F0F3]">
                      <td className="px-3 py-2">
                        {r.receiver_name}
                        {r.grouped && (
                          <button
                            type="button"
                            data-testid={`bca-group-toggle-${i}`}
                            onClick={() => setExpanded((s) => ({ ...s, [i]: !s[i] }))}
                            className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#EEF3FA] px-2 py-0.5 text-[11px] font-medium text-[#0071E3]"
                          >
                            <Users className="h-3 w-3" /> Includes {r.member_count}
                            <ChevronDown className={`h-3 w-3 transition-transform ${expanded[i] ? "rotate-180" : ""}`} />
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2">{r.bank}</td>
                      <td className="px-3 py-2">{r.transfer_type}</td>
                      <td className="num px-3 py-2">{r.credited_account}</td>
                      <td className="num px-3 py-2 text-right">Rp {rp(r.amount)}</td>
                    </tr>
                    {r.grouped && expanded[i] &&
                      r.members.map((m, j) => (
                        <tr key={`${i}-${j}`} className="bg-[#FAFAFA] text-[12px] text-[#6E6E73]">
                          <td className="px-3 py-1.5 pl-8" colSpan={4}>↳ {m.nama}</td>
                          <td className="num px-3 py-1.5 text-right">Rp {rp(m.amount)}</td>
                        </tr>
                      ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          )
        ) : null}

        <button
          data-testid={TID.bca.download}
          onClick={download}
          disabled={!canDownload}
          className="mt-6 flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#0071E3] text-[15px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.99] disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
          Download BCA File
        </button>
      </DialogContent>
    </Dialog>
  );
};

export default function Payroll() {
  const { readOnly } = useAuth();
  const { id } = useParams();
  const { t, months } = useT();
  const navigate = useNavigate();
  const [period, setPeriod] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [potonganFor, setPotonganFor] = useState(null);
  const [pendapatanFor, setPendapatanFor] = useState(null);
  const [hpMap, setHpMap] = useState({});
  const [jabMap, setJabMap] = useState({});
  const [q, setQ] = useState("");
  const [bcaOpen, setBcaOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    api
      .get("/employees")
      .then(({ data }) => {
        setHpMap(Object.fromEntries(data.map((e) => [e.id, e.no_hp || ""])));
        setJabMap(Object.fromEntries(data.map((e) => [e.id, e.jabatan || ""])));
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/periods/${id}`);
      setPeriod(data);
      setEntries(data.entries);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // keep latest dirty/saving in refs so the polling loop reads fresh values
  const dirtyRef = useRef(dirty);
  const savingRef = useRef(saving);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);
  useEffect(() => { savingRef.current = saving; }, [saving]);

  // Auto-refresh: pull the latest saved period every 15s so edits made by the
  // jarit operator (Monika) on the attendance page — and jarit ledger changes —
  // appear here automatically. Skipped while the admin has unsaved edits.
  useEffect(() => {
    const iv = setInterval(async () => {
      if (dirtyRef.current || savingRef.current) return;
      try {
        const { data } = await api.get(`/periods/${id}`);
        setEntries((list) =>
          JSON.stringify(list) === JSON.stringify(data.entries) ? list : data.entries
        );
      } catch { /* silent: transient network */ }
    }, 15000);
    return () => clearInterval(iv);
  }, [id]);

  const rows = useMemo(() => entries.map(computeEntry), [entries]);
  const summary = useMemo(() => summarize(rows), [rows]);

  const update = (employeeId, patch) => {
    setEntries((list) =>
      list.map((e) => (e.employee_id === employeeId ? { ...e, ...patch } : e))
    );
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/periods/${id}`, { entries });
      setPeriod(data);
      setEntries(data.entries);
      setDirty(false);
      toast.success("Saved");
      return data;
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const sync = async () => {
    try {
      const { data } = await api.post(`/periods/${id}/sync`);
      setPeriod(data);
      setEntries(data.entries);
      setDirty(false);
      toast.success("Synced");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const refreshBank = async () => {
    try {
      const { data } = await api.post(`/periods/${id}/refresh-master`);
      setPeriod(data);
      setEntries(data.entries);
      setDirty(false);
      toast.success("Data karyawan diperbarui (nama, NIP, bank, rate/hari) dari master");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const toggleJaritLock = async () => {
    try {
      const url = period.jarit_locked ? `/periods/${id}/unlock-jarit` : `/periods/${id}/lock-jarit`;
      await api.post(url);
      toast.success(period.jarit_locked ? "Input Monika dibuka" : "Input Monika dikunci");
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const download = async (kind, employeeIds) => {
    try {
      if (dirty) await save();
      const url =
        kind === "rekap"
          ? `/periods/${id}/pdf/rekap`
          : kind === "excel"
            ? `/periods/${id}/xlsx/rekap`
            : `/periods/${id}/pdf/slip`;
      const body = employeeIds && employeeIds.length ? { employee_ids: employeeIds } : {};
      const res = await api.post(url, body, { responseType: "blob" });
      const disp = res.headers["content-disposition"] || "";
      const match = disp.match(/filename="?([^"]+)"?/);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data);
      link.download = match ? match[1] : "gaji.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const [waOpen, setWaOpen] = useState(false);
  const [waList, setWaList] = useState([]);
  const [waSent, setWaSent] = useState({});

  const [slipOpen, setSlipOpen] = useState(false);
  const [slipSel, setSlipSel] = useState({});
  const [slipBusy, setSlipBusy] = useState(false);

  const openSlipPicker = async () => {
    if (dirty) await save();
    setSlipSel(Object.fromEntries(rows.map((r) => [r.employee_id, true])));
    setSlipOpen(true);
  };
  const toggleAllSlips = () => {
    const allOn = rows.length > 0 && rows.every((r) => slipSel[r.employee_id]);
    if (allOn) setSlipSel({});
    else setSlipSel(Object.fromEntries(rows.map((r) => [r.employee_id, true])));
  };
  const downloadSlips = async () => {
    const ids = rows.filter((r) => slipSel[r.employee_id]).map((r) => r.employee_id);
    if (!ids.length) { toast.error("Pilih minimal satu karyawan"); return; }
    setSlipBusy(true);
    try {
      await download("slip", ids);
      setSlipOpen(false);
    } finally {
      setSlipBusy(false);
    }
  };

  const openWaBulk = async () => {
    try {
      if (dirty) await save();
      const { data } = await api.get(`/periods/${id}/whatsapp-links`, { params: { base: window.location.origin } });
      setWaList(data);
      setWaSent({});
      setWaOpen(true);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const openOneWa = (r) => {
    window.open(r.wa_url, "_blank");
    setWaSent((s) => ({ ...s, [r.employee_id]: true }));
  };

  const sendWa = async (row) => {
    if (!(row.no_hp || hpMap[row.employee_id])) {
      toast.error(`Nomor HP ${row.nama} belum diisi di data karyawan`);
      return;
    }
    const win = window.open("", "_blank");
    try {
      if (dirty) await save();
      const { data } = await api.get(`/periods/${id}/whatsapp-link/${row.employee_id}`, { params: { base: window.location.origin } });
      if (win) win.location.href = data.wa_url;
      else window.location.href = data.wa_url;
    } catch (e) {
      if (win) win.close();
      toast.error(errMsg(e));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-24 text-[14px] text-[#8E8E93]">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }
  if (!period)
    return <p className="py-24 text-center text-[14px]">{t("pay.periodNotFound")}</p>;

  const term = q.trim().toLowerCase();
  const match = (r) => !term || (r.nama || "").toLowerCase().includes(term);
  const waWithHp = waList.filter((r) => r.has_hp);
  const waNoHp = waList.length - waWithHp.length;
  const waSentCount = waWithHp.filter((r) => waSent[r.employee_id]).length;
  const slipSelCount = rows.filter((r) => slipSel[r.employee_id]).length;
  const slipAllSelected = rows.length > 0 && slipSelCount === rows.length;
  const transfer = rows.filter((r) => r.metode === "transfer" && !r.is_jarit && match(r));
  const tunai = rows.filter((r) => r.metode !== "transfer" && !r.is_jarit && match(r));
  const jarit = rows.filter((r) => r.is_jarit === true && match(r));
  const potonganRow = rows.find((r) => r.employee_id === potonganFor) || null;
  const pendapatanRow = rows.find((r) => r.employee_id === pendapatanFor) || null;

  const handlers = {
    update,
    readOnly,
    onPendapatan: setPendapatanFor,
    onPotongan: setPotonganFor,
    onSlip: (eid) => download("slip", [eid]),
    onWa: sendWa,
  };

  return (
    <div data-testid={TID.payroll.root} className="pb-28">
      <button
        onClick={() => navigate("/payroll")}
        className="flex items-center gap-1.5 text-[13.5px] font-medium text-[#8E8E93] transition-colors hover:text-[#1D1D1F]"
      >
        <ArrowLeft className="h-4 w-4" /> {t("pay.back")}
      </button>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-[22px] font-semibold tracking-tight sm:text-[30px]">
          {months[period.bulan - 1]} {period.tahun}
        </h1>
        <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [&>button]:shrink-0 [&::-webkit-scrollbar]:hidden sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0">
          {!readOnly && (
          <button
            data-testid="payroll-toggle-jarit-lock"
            onClick={toggleJaritLock}
            className={`flex h-10 items-center gap-1.5 rounded-full px-4 text-[14px] font-medium transition-all active:scale-[0.98] ${period.jarit_locked ? "bg-[#FFE8B0] text-[#8A5A00] hover:bg-[#FFE0A0]" : "bg-[#EDEDF0] text-[#1D1D1F] hover:bg-[#E3E3E8]"}`}
          >
            {period.jarit_locked ? (<><Unlock className="h-4 w-4" /> {t("pay.unlockMonika")}</>) : (<><Lock className="h-4 w-4" /> {t("pay.lockMonika")}</>)}
          </button>
          )}
          {!readOnly && (
          <button
            data-testid="payroll-refresh-bank"
            onClick={refreshBank}
            title="Tarik Nama, NIP, Jabatan, Bank, dan Rate/Hari terbaru dari data karyawan. Hari kerja & kasbon tidak berubah."
            className="flex h-10 items-center gap-1.5 rounded-full bg-[#EDEDF0] px-4 text-[14px] font-medium text-[#1D1D1F] transition-all hover:bg-[#E3E3E8] active:scale-[0.98]"
          >
            <Landmark className="h-4 w-4" /> {t("pay.updateData")}
          </button>
          )}
          {!readOnly && (
          <button
            data-testid={TID.payroll.sync}
            onClick={sync}
            className="flex h-10 items-center gap-1.5 rounded-full bg-[#3A3A3C] px-4 text-[14px] font-medium text-white transition-all hover:bg-[#2C2C2E] active:scale-[0.98]"
          >
            <RefreshCw className="h-4 w-4" /> {t("pay.sync")}
          </button>
          )}
          <button
            data-testid={TID.payroll.exportSlip}
            onClick={openSlipPicker}
            className="flex h-10 items-center gap-1.5 rounded-full bg-[#0071E3] px-4 text-[14px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.98]"
          >
            <FileText className="h-4 w-4" /> {t("pay.slip")}
          </button>
          <button
            data-testid={TID.payroll.exportExcel}
            onClick={() => download("excel")}
            className="flex h-10 items-center gap-1.5 rounded-full bg-[#128C3E] px-4 text-[14px] font-medium text-white transition-all hover:bg-[#0F7433] active:scale-[0.98]"
          >
            <FileDown className="h-4 w-4" /> {t("pay.excel")}
          </button>
          <button
            data-testid={TID.payroll.bca}
            onClick={async () => { if (dirty) await save(); setBcaOpen(true); }}
            className="flex h-10 items-center gap-1.5 rounded-full bg-[#BBDDFB] px-4 text-[14px] font-semibold text-[#0A5BB8] transition-all hover:bg-[#A6D1F8] active:scale-[0.98]"
          >
            <img src="/bca-logo.webp" alt="BCA" className="h-7 w-auto" /> {t("pay.bcaPayroll")}
          </button>
          <button
            data-testid={TID.payroll.exportRekap}
            onClick={() => download("rekap")}
            className="flex h-10 items-center gap-1.5 rounded-full bg-[#C8102E] px-4 text-[14px] font-medium text-white transition-all hover:bg-[#A60D26] active:scale-[0.98]"
          >
            <FileDown className="h-4 w-4" /> {t("pay.recap")}
          </button>
          <button
            data-testid={TID.payroll.waBulk}
            onClick={openWaBulk}
            className="flex h-10 items-center gap-1.5 rounded-full bg-[#25D366] px-4 text-[14px] font-medium text-white transition-all hover:brightness-105 active:scale-[0.98]"
          >
            <MessageCircle className="h-4 w-4" /> {t("pay.waAll")}
          </button>
        </div>
      </div>

      <BcaExportDialog periodId={id} open={bcaOpen} onOpenChange={setBcaOpen} />

      <FingerspotSyncPanel periodId={id} onChanged={load} />

      <Dialog open={slipOpen} onOpenChange={setSlipOpen}>
        <DialogContent
          data-testid={TID.payroll.slipDialog}
          className="flex max-h-[85vh] flex-col gap-0 overflow-hidden rounded-[20px] border border-white/50 bg-white/70 p-0 shadow-[0_20px_60px_rgba(0,0,0,0.18)] ring-1 ring-inset ring-white/50 backdrop-blur-2xl backdrop-saturate-150 sm:max-w-[520px]"
        >
          <div className="shrink-0 px-5 pt-5 sm:px-6 sm:pt-6">
            <DialogHeader className="text-left">
              <DialogTitle className="flex items-center gap-2 pr-8 text-[18px] font-semibold tracking-tight sm:text-[19px]">
                <FileText className="h-5 w-5 shrink-0 text-[#0071E3]" /> Download Slip Gaji
              </DialogTitle>
            </DialogHeader>
            <p className="mt-1.5 text-[13px] text-[#6E6E73]">Pilih karyawan yang slip gajinya ingin diunduh.</p>
            <button
              data-testid={TID.payroll.slipSelectAll}
              onClick={toggleAllSlips}
              className="mt-3 flex w-full items-center justify-between rounded-xl bg-[#F5F5F7] px-3.5 py-2.5 text-left transition-colors hover:bg-[#EDEDF0]"
            >
              <span className="text-[13.5px] font-semibold">{slipAllSelected ? "Batalkan Semua" : "Pilih Semua"}</span>
              <span className="text-[12.5px] font-medium text-[#6E6E73]">{slipSelCount} / {rows.length} dipilih</span>
            </button>
          </div>
          <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto px-5 py-3 sm:px-6">
            {rows.map((r) => {
              const on = !!slipSel[r.employee_id];
              return (
                <button
                  key={r.employee_id}
                  data-testid={TID.payroll.slipRow(r.employee_id)}
                  onClick={() => setSlipSel((s) => ({ ...s, [r.employee_id]: !s[r.employee_id] }))}
                  className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-black/[0.03] active:bg-black/[0.05]"
                >
                  <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-md border transition-colors ${on ? "border-[#0071E3] bg-[#0071E3] text-white" : "border-[#C9C9CE] bg-white"}`}>
                    {on && <Check className="h-3.5 w-3.5" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5"><span className="block truncate text-[14px] font-medium">{r.nama}</span><JaritBadge show={r.is_jarit} /></span>
                    <span className="block truncate text-[12px] text-[#8E8E93]">{r.emp_code ? `${r.emp_code} · ` : ""}{r.is_jarit ? "Tukang Jarit" : (jabMap[r.employee_id] || r.jabatan || "\u2014")}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <div className="shrink-0 border-t border-black/[0.06] bg-white/40 px-5 py-4 sm:px-6">
            <button
              data-testid={TID.payroll.slipDownload}
              onClick={downloadSlips}
              disabled={slipBusy || slipSelCount === 0}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#0071E3] text-[15px] font-semibold text-white shadow-[0_10px_26px_rgba(0,113,227,0.35)] transition-all hover:bg-[#0077ED] active:scale-[0.98] disabled:opacity-50"
            >
              {slipBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              Download {slipSelCount > 0 ? `(${slipSelCount})` : ""} Slip
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={waOpen} onOpenChange={setWaOpen}>
        <DialogContent
          data-testid={TID.payroll.waDialog}
          className="flex max-h-[85vh] flex-col gap-0 overflow-hidden rounded-[20px] border border-white/50 bg-white/70 p-0 shadow-[0_20px_60px_rgba(0,0,0,0.18)] ring-1 ring-inset ring-white/50 backdrop-blur-2xl backdrop-saturate-150 sm:max-w-[520px]"
        >
          <div className="shrink-0 px-5 pt-5 sm:px-6 sm:pt-6">
            <DialogHeader className="text-left">
              <DialogTitle className="flex items-center gap-2 pr-8 text-[18px] font-semibold tracking-tight sm:text-[19px]">
                <MessageCircle className="h-5 w-5 shrink-0 text-[#128C3E]" /> Kirim Slip via WhatsApp
              </DialogTitle>
            </DialogHeader>
            <p className="mt-1.5 text-[13px] leading-snug text-[#6E6E73]">
              Tekan "Buka WA" pada tiap karyawan — WhatsApp akan terbuka dengan pesan &amp; link slip siap kirim. Tinggal tekan Kirim di WhatsApp.
            </p>
            <div className="mt-3 flex items-center justify-between rounded-xl bg-[#F5F5F7] px-3.5 py-2 text-[12.5px] font-medium text-[#6E6E73]">
              <span>{waSentCount} / {waWithHp.length} dibuka</span>
              {waNoHp > 0 && <span>{waNoHp} tanpa no. HP</span>}
            </div>
          </div>
          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-5 py-3 sm:px-6">
            {waList.map((r) => (
              <div key={r.employee_id} className="flex items-center justify-between gap-3 rounded-xl bg-white/70 px-3 py-2.5">
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 text-[14px] font-medium"><span className="truncate">{r.nama}</span>{r.emp_code ? <span className="num text-[11px] font-medium text-[#0071E3]">{r.emp_code}</span> : null}<JaritBadge show={r.is_jarit} /></p>
                  <p className="num text-[12px] text-[#8E8E93]">{r.has_hp ? r.to : "No. HP belum diisi"}</p>
                </div>
                {r.has_hp ? (
                  <button
                    data-testid={TID.payroll.waBulkOpen(r.employee_id)}
                    onClick={() => openOneWa(r)}
                    className={`flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 text-[13px] font-medium transition-all active:scale-95 ${
                      waSent[r.employee_id] ? "bg-[#E8F8EC] text-[#128C3E]" : "bg-[#25D366] text-white hover:brightness-105"
                    }`}
                  >
                    {waSent[r.employee_id] ? (
                      <><Check className="h-4 w-4" /> Dibuka</>
                    ) : (
                      <><MessageCircle className="h-4 w-4" /> Buka WA</>
                    )}
                  </button>
                ) : (
                  <span className="shrink-0 rounded-full bg-[#F2F2F7] px-3 py-1 text-[12px] text-[#B0B0B8]">—</span>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[
          {
            tid: TID.payroll.summaryTransfer,
            label: t("pay.transfer"),
            value: summary.subtotal_transfer,
            dark: false,
          },
          {
            tid: TID.payroll.summaryTunai,
            label: t("pay.cash"),
            value: summary.subtotal_tunai,
            dark: false,
          },
          {
            tid: TID.payroll.summaryJarit,
            label: t("pay.tailors"),
            value: summary.subtotal_jarit,
            dark: false,
          },
          {
            tid: TID.payroll.summaryTotal,
            label: t("pay.total"),
            value: summary.total_keseluruhan,
            dark: true,
          },
        ].map((c) => (
          <div
            key={c.label}
            data-testid={c.tid}
            className="card-soft px-5 py-4"
            style={c.dark ? { background: "#0071E3" } : undefined}
          >
            <span className={`label ${c.dark ? "!text-white/60" : ""}`}>{c.label}</span>
            <p
              className={`num mt-1.5 text-[22px] font-semibold tracking-tight ${
                c.dark ? "text-white" : ""
              }`}
            >
              Rp {rp(c.value)}
            </p>
          </div>
        ))}
      </div>

      <div className="relative mt-6 max-w-sm">
        <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#B0B0B8]" />
        <input
          data-testid={TID.payroll.search}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("pay.searchPlaceholder")}
          className="focus-ring h-11 w-full rounded-full border border-[#E1E1E6] bg-white pl-10 pr-4 text-[15px] placeholder:text-[#B0B0B8]"
        />
      </div>

      <Section title={t("pay.transfer")} list={transfer} mobile={isMobile} {...handlers} />
      <Section title={t("pay.cash")} list={tunai} mobile={isMobile} {...handlers} />
      <JaritSection list={jarit} mobile={isMobile} onSlip={handlers.onSlip} onWa={handlers.onWa} />
      {term && transfer.length === 0 && tunai.length === 0 && jarit.length === 0 && (
        <p className="card-soft mt-5 px-5 py-10 text-center text-[14px] text-[#8E8E93]">
          {t("pay.notFound", { q })}
        </p>
      )}

      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-black/[0.06] bg-white/85 px-5 py-3 backdrop-blur-xl sm:px-8">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-4">
          <div>
            <span className="label">{months[period.bulan - 1]} {period.tahun}</span>
            <p className="num text-[17px] font-semibold tracking-tight">
              Rp {rp(summary.total_keseluruhan)}
            </p>
          </div>
          {!readOnly && (
          <button
            data-testid={TID.payroll.save}
            onClick={save}
            disabled={saving || !dirty}
            className={`flex h-10 items-center gap-1.5 rounded-full px-5 text-[14px] font-medium transition-all active:scale-[0.98] ${
              dirty
                ? "bg-[#0071E3] text-white hover:bg-[#0077ED]"
                : "bg-[#F2F2F7] text-[#8E8E93]"
            }`}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {dirty ? t("pay.save") : t("pay.saved")}
          </button>
          )}
        </div>
      </div>

      <ItemsDialog
        open={!!pendapatanFor}
        onOpenChange={(v) => !v && setPendapatanFor(null)}
        title="Pendapatan Lainnya"
        presets={[
          { key: "lembur", label: "Lembur" },
          { key: "thr", label: "THR" },
          { key: "insentif", label: "Insentif" },
        ]}
        items={pendapatanRow?.pendapatan_items}
        rowKey={pendapatanFor}
        onChange={(items) => update(pendapatanFor, { pendapatan_items: items })}
        tid={TID.pendapatan}
      />

      <ItemsDialog
        open={!!potonganFor}
        onOpenChange={(v) => !v && setPotonganFor(null)}
        title="Potongan Lain"
        presets={[
          { key: "titip", label: "TITIP" },
          { key: "kasbon-baju", label: "Kasbon Baju" },
          { key: "perlengkapan", label: "Perlengkapan" },
        ]}
        items={potonganRow?.potongan_items}
        rowKey={potonganFor}
        onChange={(items) => update(potonganFor, { potongan_items: items })}
        tid={TID.potongan}
      />
    </div>
  );
}
